import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/api';
import toast from 'react-hot-toast';
import './ManageVastuConsultations.css';

const currency = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 });

const statusOptions = ['payment_pending', 'payment_submitted', 'paid', 'under_review', 'more_info_needed', 'scheduled', 'completed', 'report_uploaded', 'cancelled', 'refunded'];
const formatStatus = (status) => String(status || 'pending').replace(/_/g, ' ');
const toDateTimeInputValue = (date) => {
  if (!date) return '';
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

const ManageVastuConsultations = () => {
  const { user, hasVastuConsultantAccess } = useAuth();
  const [summary, setSummary] = useState(null);
  const [consultations, setConsultations] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // --- UI States ---
  const [expandedId, setExpandedId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [paymentFilter, setPaymentFilter] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // --- Draft States for Editing ---
  const [savingId, setSavingId] = useState('');
  const [meetingDrafts, setMeetingDrafts] = useState({});
  const [paymentDrafts, setPaymentDrafts] = useState({});
  const [adminNotesDrafts, setAdminNotesDrafts] = useState({});

  const isConsultantMode = user?.role !== 'admin' && (['consultant', 'vastu_consultant'].includes(user?.role) || hasVastuConsultantAccess);
  const consultantOptions = useMemo(() => consultants.filter((item) => item.isActive), [consultants]);

  const buildDrafts = (items) => {
    const mDrafts = {}; const pDrafts = {}; const nDrafts = {};
    (items || []).forEach(item => {
      mDrafts[item._id] = { scheduledAt: toDateTimeInputValue(item.scheduledAt), meetingLink: item.meetingLink || '' };
      pDrafts[item._id] = { amount: item.paymentInstructions?.amount ?? item.payment?.amount ?? '', upiId: item.paymentInstructions?.upiId || '', paymentLink: item.paymentInstructions?.paymentLink || '', notes: item.paymentInstructions?.notes || '' };
      nDrafts[item._id] = item.adminNotes || '';
    });
    setMeetingDrafts(prev => ({...prev, ...mDrafts}));
    setPaymentDrafts(prev => ({...prev, ...pDrafts}));
    setAdminNotesDrafts(prev => ({...prev, ...nDrafts}));
  };

  const loadData = async () => {
    setIsLoading(true);
    try {
      if (isConsultantMode) {
        const response = await apiService.getVastuConsultantPortalConsultations();
        setConsultations(response.data?.consultations || []);
        setConsultants(response.data?.consultant ? [response.data.consultant] : []);
        buildDrafts(response.data?.consultations);
      } else {
        const [summaryRes, consultationsRes, consultantsRes] = await Promise.all([
          apiService.getVastuAdminSummary(),
          apiService.getVastuAdminConsultations(),
          apiService.getVastuAdminConsultants(),
        ]);
        setSummary(summaryRes.data);
        setConsultations(consultationsRes.data || []);
        setConsultants(consultantsRes.data || []);
        buildDrafts(consultationsRes.data);
      }
    } catch (err) { toast.error('Failed to load data'); } 
    finally { setIsLoading(false); }
  };

  useEffect(() => { loadData(); }, [isConsultantMode]);

  // --- Filtering & Pagination Logic ---
  const processedData = useMemo(() => {
    let result = consultations.filter(item => {
      const searchStr = searchTerm.toLowerCase();
      const matchesSearch = item.bookingCode?.toLowerCase().includes(searchStr) || 
                            item.customer?.fullName?.toLowerCase().includes(searchStr) || 
                            item.customer?.email?.toLowerCase().includes(searchStr);
      const matchesStatus = statusFilter === 'All' || item.status === statusFilter;
      const matchesPayment = paymentFilter === 'All' || item.payment?.status === paymentFilter;
      return matchesSearch && matchesStatus && matchesPayment;
    });
    return result;
  }, [consultations, searchTerm, statusFilter, paymentFilter]);

  const totalPages = Math.ceil(processedData.length / itemsPerPage);
  const currentData = processedData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  // --- Handlers ---
  const toggleExpand = (id) => setExpandedId(expandedId === id ? null : id);
  
  const updateConsultation = async (id, payload) => {
    setSavingId(id);
    try {
      const response = isConsultantMode 
        ? await apiService.updateVastuConsultantPortalConsultation(id, payload)
        : await apiService.updateVastuAdminConsultation(id, payload);
      setConsultations(cur => cur.map(item => item._id === id ? response.data : item));
      toast.success('Updated successfully');
    } catch (err) { toast.error(err.message); } 
    finally { setSavingId(''); }
  };

  const saveMeetingDetails = async (item) => {
    const draft = meetingDrafts[item._id] || {};
    const hasScheduledAt = !!draft.scheduledAt;
    const nextScheduledAt = hasScheduledAt ? new Date(draft.scheduledAt).toISOString() : null;
    const shouldMoveToScheduled = hasScheduledAt && draft.meetingLink && ['paid', 'under_review', 'more_info_needed'].includes(item.status);
    await updateConsultation(item._id, { scheduledAt: nextScheduledAt, meetingLink: draft.meetingLink || '', ...(shouldMoveToScheduled ? { status: 'scheduled' } : {}), adminNotes: adminNotesDrafts[item._id] });
  };

  const savePaymentInstructions = async (item) => {
    const draft = paymentDrafts[item._id] || {};
    const formData = new FormData();
    formData.append('amount', draft.amount ?? item.payment?.amount ?? 0);
    formData.append('currency', item.payment?.currency || item.paymentInstructions?.currency || 'INR');
    formData.append('upiId', draft.upiId || '');
    formData.append('paymentLink', draft.paymentLink || '');
    formData.append('notes', draft.notes || '');
    if (draft.qrCode) formData.append('qrCode', draft.qrCode);

    setSavingId(item._id);
    try {
      const response = isConsultantMode 
        ? await apiService.updateVastuConsultantPortalPaymentInstructions(item._id, formData)
        : await apiService.updateVastuAdminPaymentInstructions(item._id, formData);
      setConsultations(cur => cur.map(c => c._id === item._id ? response.data : c));
      toast.success('Payment instructions saved');
    } catch (err) { toast.error(err.message); } 
    finally { setSavingId(''); }
  };

  const verifyPaymentProof = async (item, action) => {
    setSavingId(item._id);
    try {
      const response = isConsultantMode
        ? await apiService.verifyVastuConsultantPortalPaymentProof(item._id, { action })
        : await apiService.verifyVastuAdminPaymentProof(item._id, { action });
      setConsultations(cur => cur.map(c => c._id === item._id ? response.data : c));
      toast.success(`Payment ${action}`);
    } catch (err) { toast.error(err.message); } 
    finally { setSavingId(''); }
  };

  const downloadFile = async (item, file, type) => {
    try {
      if (type === 'asset') {
        isConsultantMode ? await apiService.downloadVastuConsultantPortalPaymentAsset(item._id, file.type, file.name) : await apiService.downloadVastuAdminPaymentAsset(item._id, file.type, file.name);
      } else {
        isConsultantMode ? await apiService.downloadVastuConsultantPortalFile(item._id, file._id, file.originalName) : await apiService.downloadVastuAdminFile(item._id, file._id, file.originalName);
      }
    } catch (err) { toast.error('Download failed'); }
  };

  if (isLoading) return <div className="loading-spinner">Loading consultations...</div>;

  return (
    <div className="manage-vastu-container">
      <div className="header-section">
        <div>
          <h2>{isConsultantMode ? 'Vastu Consultant Desk' : 'Manage Vastu Consultations'}</h2>
          <p>Review bookings, manage payments, and update schedules.</p>
        </div>
        <button className="vastu-refresh-btn" onClick={loadData}><i className="fas fa-sync-alt"></i> Refresh</button>
      </div>

      {/* --- TOOLBAR --- */}
      <div className="admin-toolbar" style={{ marginBottom: '2rem' }}>
        <div className="search-box">
          <i className="fas fa-search"></i>
          <input type="text" placeholder="Search Booking Code, Name, Email..." value={searchTerm} onChange={(e) => {setSearchTerm(e.target.value); setCurrentPage(1);}} />
        </div>
        <div className="filters-group">
          <select value={statusFilter} onChange={(e) => {setStatusFilter(e.target.value); setCurrentPage(1);}}>
            <option value="All">All Statuses</option>
            {statusOptions.map(opt => <option key={opt} value={opt}>{formatStatus(opt)}</option>)}
          </select>
          <select value={paymentFilter} onChange={(e) => {setPaymentFilter(e.target.value); setCurrentPage(1);}}>
            <option value="All">All Payments</option>
            <option value="pending">Pending</option>
            <option value="submitted">Submitted</option>
            <option value="paid">Paid</option>
          </select>
        </div>
      </div>

      <div className="consultation-accordion-list admin-accordion-list">
        {currentData.length > 0 ? currentData.map((item) => {
          const isExpanded = expandedId === item._id;
          return (
            <article className={`consultation-accordion-card ${isExpanded ? 'expanded' : ''}`} key={item._id}>
              
              {/* HEADER (Always Visible) */}
              <div className="accordion-header" onClick={() => toggleExpand(item._id)}>
                <div style={{ display: 'flex', gap: '2rem', alignItems: 'center', flexGrow: 1 }}>
                  <div>
                    <div className="booking-code">{item.bookingCode}</div>
                    <strong>{item.customer?.fullName}</strong>
                  </div>
                  <div>
                    <small style={{display:'block', color:'#666'}}>Type</small>
                    <span style={{fontWeight:'600'}}>{item.consultationType?.name}</span>
                  </div>
                  {!isConsultantMode && (
                    <div>
                      <small style={{display:'block', color:'#666'}}>Consultant</small>
                      <span style={{fontWeight:'600', color:'var(--vastu-green)'}}>{item.consultant?.displayName || 'Unassigned'}</span>
                    </div>
                  )}
                </div>
                <div className="header-secondary-info">
                  <span className={`status-pill status-${item.payment?.status || 'pending'}`}>Pay: {item.payment?.status}</span>
                  <span className={`status-pill status-${item.status}`}>{formatStatus(item.status)}</span>
                  <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'} expand-icon`}></i>
                </div>
              </div>

              {/* BODY (Expanded) */}
              {isExpanded && (
                <div className="accordion-body admin-accordion-body">
                  
                  {/* Row 1: Info & Consultant Assignment */}
                  <div className="admin-grid-3">
                    <div className="info-box">
                      <h4>Customer Info</h4>
                      <p><i className="fas fa-envelope"></i> {item.customer?.email}</p>
                      <p><i className="fas fa-phone"></i> {item.customer?.phone}</p>
                      <p><i className="fas fa-language"></i> Lang: {item.preferredLanguage}</p>
                    </div>
                    <div className="info-box">
                      <h4>Property Details</h4>
                      <p><strong>Type:</strong> {item.property?.propertyType}</p>
                      <p><strong>Facing:</strong> {item.property?.facingDirection || 'N/A'}</p>
                      <p><strong>Address:</strong> {item.property?.city}, {item.property?.state}</p>
                      <div style={{marginTop:'10px'}}>
                        <strong>Concerns:</strong>
                        <p style={{fontSize:'0.85rem', color:'#555', marginTop:'4px'}}>{item.property?.concerns || 'None'}</p>
                      </div>
                    </div>
                    <div className="info-box">
                      <h4>Management</h4>
                      {!isConsultantMode && (
                        <div style={{ marginBottom: '10px' }}>
                          <label>Assign Consultant:</label>
                          <select value={item.consultant?._id || ''} onChange={(e) => updateConsultation(item._id, { consultantId: e.target.value })} style={{ padding: '8px', width: '100%', marginTop: '5px' }}>
                            <option value="">Unassigned</option>
                            {consultantOptions.map(c => <option key={c._id} value={c._id}>{c.displayName}</option>)}
                          </select>
                        </div>
                      )}
                      <div>
                        <label>Override Status:</label>
                        <select value={item.status} onChange={(e) => updateConsultation(item._id, { status: e.target.value })} style={{ padding: '8px', width: '100%', marginTop: '5px' }}>
                           {statusOptions.map(opt => <option key={opt} value={opt}>{formatStatus(opt)}</option>)}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Row 2: Files & Notes */}
                  <div className="admin-grid-2">
                    <div className="info-box">
                      <h4><i className="fas fa-folder"></i> Uploaded Documents</h4>
                      {(item.uploadedFiles || []).length > 0 ? (
                        <div style={{display:'flex', flexWrap:'wrap', gap:'8px', marginTop:'10px'}}>
                          {item.uploadedFiles.map(file => (
                            <button key={file._id} className="vastu-file-btn" onClick={() => downloadFile(item, file, 'doc')}>
                              <i className="fas fa-download"></i> {file.originalName.length > 20 ? file.originalName.substring(0,20)+'...' : file.originalName}
                            </button>
                          ))}
                        </div>
                      ) : <p className="text-muted">No files uploaded.</p>}
                    </div>

                    {/* --- NEW INTERNAL NOTES SECTION --- */}
                    <div className="info-box admin-notes-box">
                      <h4><i className="fas fa-lock"></i> Internal Notes</h4>
                      <label style={{fontSize:'0.85rem'}}>Consultant's Private Notes (Read-Only):</label>
                      <div style={{ background:'#f8f9fa', padding:'10px', borderRadius:'6px', border:'1px solid #eee', marginBottom:'10px', minHeight:'50px', fontSize:'0.9rem', color:'#555', whiteSpace:'pre-wrap' }}>
                        {item.consultantNotes || 'No notes added by consultant yet.'}
                      </div>
                      
                      {!isConsultantMode && (
                        <>
                          <label style={{fontSize:'0.85rem'}}>Admin Notes:</label>
                          <textarea 
                            rows="2" 
                            value={adminNotesDrafts[item._id] || ''} 
                            onChange={(e) => setAdminNotesDrafts({...adminNotesDrafts, [item._id]: e.target.value})}
                            placeholder="Add private admin notes here..."
                            style={{width:'100%', padding:'8px', borderRadius:'6px', border:'1px solid #ccc', fontSize:'0.9rem'}}
                          />
                        </>
                      )}
                    </div>
                  </div>

                  {/* Row 3: Meeting & Schedule */}
                  <div className="info-box" style={{ background: '#f0fbf7', borderColor: '#a8dccb' }}>
                    <h4 style={{ color: 'var(--vastu-green)' }}><i className="fas fa-video"></i> Schedule & Meeting Link</h4>
                    <div style={{ display:'flex', gap:'15px', alignItems:'flex-end', flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: '200px' }}>
                        <label>Date & Time</label>
                        <input type="datetime-local" value={meetingDrafts[item._id]?.scheduledAt || ''} onChange={(e) => setMeetingDrafts({...meetingDrafts, [item._id]: {...meetingDrafts[item._id], scheduledAt: e.target.value}})} style={{padding:'10px', width:'100%'}}/>
                      </div>
                      <div style={{ flex: 2, minWidth: '250px' }}>
                        <label>Video Meeting Link</label>
                        <input type="url" value={meetingDrafts[item._id]?.meetingLink || ''} onChange={(e) => setMeetingDrafts({...meetingDrafts, [item._id]: {...meetingDrafts[item._id], meetingLink: e.target.value}})} placeholder="https://zoom.us/..." style={{padding:'10px', width:'100%'}}/>
                      </div>
                      <button className="primary-btn" onClick={() => saveMeetingDetails(item)} disabled={savingId === item._id}>
                        {savingId === item._id ? 'Saving...' : 'Save Schedule'}
                      </button>
                    </div>
                  </div>

                  {/* Row 4: Payment Management */}
                  <div className="admin-grid-2">
                     <div className="info-box">
                       <h4>Manage Payment Instructions</h4>
                       <p className="text-muted" style={{fontSize:'0.85rem', marginBottom:'10px'}}>Update details sent to the user.</p>
                       <div style={{display:'grid', gap:'10px'}}>
                         <input type="number" placeholder="Amount (₹)" value={paymentDrafts[item._id]?.amount ?? ''} onChange={(e) => setPaymentDrafts({...paymentDrafts, [item._id]: {...paymentDrafts[item._id], amount: e.target.value}})}/>
                         <input type="text" placeholder="UPI ID" value={paymentDrafts[item._id]?.upiId || ''} onChange={(e) => setPaymentDrafts({...paymentDrafts, [item._id]: {...paymentDrafts[item._id], upiId: e.target.value}})}/>
                         <input type="url" placeholder="Payment Link" value={paymentDrafts[item._id]?.paymentLink || ''} onChange={(e) => setPaymentDrafts({...paymentDrafts, [item._id]: {...paymentDrafts[item._id], paymentLink: e.target.value}})}/>
                         <input type="file" accept="image/*" onChange={(e) => setPaymentDrafts({...paymentDrafts, [item._id]: {...paymentDrafts[item._id], qrCode: e.target.files[0]}})} />
                         {item.paymentInstructions?.qrCodeFile && (
                           <small style={{color:'var(--vastu-green)'}}>✓ Active QR Code: {item.paymentInstructions.qrCodeFile.originalName}</small>
                         )}
                         <button className="secondary-action-btn" style={{borderColor:'var(--primary-color)', color:'var(--primary-color)', background:'transparent'}} onClick={() => savePaymentInstructions(item)}>Save Instructions</button>
                       </div>
                     </div>

                     <div className="info-box">
                       <h4>Verify Payment Proof</h4>
                       <div style={{ background: '#fdfdfd', border: '1px dashed #ccc', padding: '15px', borderRadius: '8px', height: '100%', display:'flex', flexDirection:'column' }}>
                         <p><strong>Status:</strong> <span style={{textTransform:'uppercase', color: item.paymentProof?.verificationStatus==='pending' ? '#f59e0b' : 'inherit'}}>{formatStatus(item.paymentProof?.verificationStatus || 'not_submitted')}</span></p>
                         <p><strong>Transaction Ref:</strong> {item.paymentProof?.transactionReference || 'N/A'}</p>
                         
                         {item.paymentProof?.proofFile && (
                           <button className="vastu-file-btn" style={{marginTop:'10px'}} onClick={() => downloadFile(item, {type: 'proof', name: item.paymentProof.proofFile.originalName}, 'asset')}>
                             <i className="fas fa-download"></i> Download Proof Image
                           </button>
                         )}

                         <div style={{ display: 'flex', gap: '10px', marginTop: 'auto', paddingTop: '15px' }}>
                           <button className="primary-btn" style={{flex:1, background:'#28a745', padding:'8px'}} onClick={() => verifyPaymentProof(item, 'verified')} disabled={!item.paymentProof?.transactionReference && !item.paymentProof?.proofFile}>Verify & Mark Paid</button>
                           <button className="primary-btn" style={{flex:1, background:'#dc3545', padding:'8px'}} onClick={() => verifyPaymentProof(item, 'rejected')} disabled={!item.paymentProof?.transactionReference && !item.paymentProof?.proofFile}>Reject</button>
                         </div>
                       </div>
                     </div>
                  </div>

                </div>
              )}
            </article>
          );
        }) : (
          <div className="empty-state">No consultations found matching your criteria.</div>
        )}
      </div>

      {/* --- PAGINATION --- */}
      {totalPages > 1 && (
        <div className="pagination-controls" style={{ marginTop: '2rem', display: 'flex', justifyContent: 'center', gap: '1rem', alignItems: 'center' }}>
          <button onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #ccc', background: '#fff', cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}>
            <i className="fas fa-chevron-left"></i> Prev
          </button>
          <span style={{ fontWeight: '600', color: '#555' }}>Page {currentPage} of {totalPages}</span>
          <button onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages} style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #ccc', background: '#fff', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer' }}>
            Next <i className="fas fa-chevron-right"></i>
          </button>
        </div>
      )}
    </div>
  );
};

export default ManageVastuConsultations;